import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigation, useFocusEffect, CommonActions } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { logout, refreshUser, setUser } from '@/store/slices/authSlice';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { AuthAPI } from '@/services/residents/api/AuthAPI';
import { formatDisplayName } from '@/utils/residents/user';
import { buildFeedbackUrl } from '@/utils/residents/links';
import { showAlert, showConfirm } from '@/utils/common/alert';
import { profileStrings as strings, logoutStrings, commonStrings } from '@/constants/residents/strings';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { logger } from '@/utils/common/logger';

export function useSettingsScreen() {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { linkedAccounts } = useAppSelector((state) => state.home);
  const { mode, toggleTheme } = useThemeColors();
  const isDarkMode = mode === 'dark';

  const shouldShowPayments = user?.customerType !== 'non-leasing occupant';

  useFocusEffect(
    useCallback(() => {
      dispatch(refreshUser());
      dispatch(fetchLinkedAccounts());
    }, [dispatch]),
  );

  // --- User display ---
  const displayName = useMemo(() => formatDisplayName(user as any), [user]);
  const email = user?.email || '';
  const propertyName = (user as any)?.property?.marketingName || (user as any)?.property?.legalName || '';
  const unitNumber = user?.unitNumberSpace || '';
  const propertyUnit = useMemo(() => {
    const parts: string[] = [];
    if (propertyName) parts.push(propertyName);
    if (unitNumber) parts.push(`Unit ${unitNumber}`);
    return parts.join(' · ');
  }, [propertyName, unitNumber]);

  const initials = useMemo(() => {
    const parts = displayName.split(' ').filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return '';
  }, [displayName]);

  // --- App version ---
  const [appVersion, setAppVersion] = useState('');
  useEffect(() => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const pkg = require('../../../package.json');
      setAppVersion(pkg.version ? `Resio v${pkg.version}` : '');
    } catch {
      setAppVersion('');
    }
  }, []);

  // --- WiFi feature flag ---
  const isWifiFeatureEnabled = useMemo(() => {
    try {
      const flags = (user as any)?.property?.featureFlags?.flags || {};
      if (typeof flags.wifiEnabled === 'boolean') return !!flags.wifiEnabled;
      if (typeof flags['cbx-wifi-integration-enabled'] === 'boolean') return !!flags['cbx-wifi-integration-enabled'];
      return false;
    } catch {
      return false;
    }
  }, [user]);

  // --- Password change ---
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  const handleChangePassword = useCallback(async () => {
    const userEmail = user?.email;
    if (!userEmail) {
      showAlert('Error', 'No email associated with this account.');
      return;
    }
    try {
      if (isMounted.current) setIsSendingVerification(true);
      const result = await AuthAPI.sendVerification(userEmail);
      if (result?.success) {
        navigation.navigate('AccountVerification', { email: userEmail, isSignup: false });
      } else {
        showAlert('Error', result?.message || 'Failed to send verification email.');
      }
    } catch (e: any) {
      showAlert('Error', e?.message || 'Failed to send verification email.');
    } finally {
      if (isMounted.current) setIsSendingVerification(false);
    }
  }, [navigation, user?.email]);

  // --- Notification toggles ---
  const communityUpdatesEnabled = !!user?.enablePushNotificationMessages;

  const handleToggleCommunityUpdates = useCallback(async (val: boolean) => {
    try {
      const updated = await AuthAPI.updateUser({ enablePushNotificationMessages: val });
      dispatch(setUser(updated));
    } catch (e) {
      logger.warn('settingsHookToggleFailed', e);
    }
  }, [dispatch]);

  // --- Roommate sharing (staged, batch on blur) ---
  const roommateEmailValue = !!user?.roommatesShareEmail;
  const roommatePhoneValue = !!user?.roommatesSharePhone;
  const [stagedRoommateEmail, setStagedRoommateEmail] = useState(roommateEmailValue);
  const [stagedRoommatePhone, setStagedRoommatePhone] = useState(roommatePhoneValue);

  useFocusEffect(
    useCallback(() => {
      setStagedRoommateEmail(roommateEmailValue);
      setStagedRoommatePhone(roommatePhoneValue);
    }, [roommateEmailValue, roommatePhoneValue]),
  );

  useFocusEffect(
    useCallback(() => {
      return () => {
        if (!user) return;
        const phoneChanged = stagedRoommatePhone !== !!user.roommatesSharePhone;
        const emailChanged = stagedRoommateEmail !== !!user.roommatesShareEmail;
        if (!phoneChanged && !emailChanged) return;
        const payload: any = {};
        if (phoneChanged) payload.roommatesSharePhone = stagedRoommatePhone;
        if (emailChanged) payload.roommatesShareEmail = stagedRoommateEmail;
        (async () => {
          try {
            const updated = await AuthAPI.updateUser(payload);
            dispatch(setUser(updated));
          } catch (e) {
            logger.warn('settingsHookSaveFailed', e);
          }
        })();
      };
    }, [stagedRoommatePhone, stagedRoommateEmail, user, dispatch]),
  );

  // --- Logout ---
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = useCallback(async () => {
    const confirmed = await showConfirm(
      'Confirm Logout',
      logoutStrings.question,
      logoutStrings.button,
      'Cancel',
    );
    if (!confirmed) return;
    try {
      setIsLoggingOut(true);
      await dispatch(logout()).unwrap();
    } catch {
      // logout thunk handles cleanup gracefully
    } finally {
      setIsLoggingOut(false);
    }
  }, [dispatch]);

  // --- Navigation handlers ---
  const handleProfilePress = useCallback(() => navigation.navigate('ProfileBio'), [navigation]);
  const handleLinkedAccountsPress = useCallback(() => {
    navigation.dispatch(
      CommonActions.navigate({
        name: 'Main',
        params: { screen: 'ResidentLinkedAccounts' },
      })
    );
  }, [navigation]);
  const handlePaymentMethodsPress = useCallback(() => navigation.navigate('MobileAccountSettings', { section: 'paymentMethods' }), [navigation]);
  const handleAutopayPress = useCallback(() => navigation.navigate('MobileAccountSettings', { section: 'autopay' }), [navigation]);
  const handleHowItWorksPress = useCallback(() => navigation.navigate('HowItWorks'), [navigation]);
  const handleTutorialPress = useCallback(() => navigation.navigate('Tutorial'), [navigation]);
  const handleDeleteAccountPress = useCallback(() => navigation.navigate('DeleteAccount'), [navigation]);

  const handleFeedbackPress = useCallback(() => {
    const url = buildFeedbackUrl(user?.email);
    navigation.navigate('WebView', { url, title: strings.option.feedback });
  }, [navigation, user?.email]);

  const handleToggleTheme = useCallback(() => {
    toggleTheme();
  }, [toggleTheme]);

  return {
    displayName,
    email,
    propertyUnit,
    initials,
    appVersion,
    shouldShowPayments,
    isWifiFeatureEnabled,
    isSendingVerification,
    isLoggingOut,
    linkedAccounts,
    communityUpdatesEnabled,
    stagedRoommateEmail,
    stagedRoommatePhone,
    isDarkMode,
    handleChangePassword,
    handleToggleCommunityUpdates,
    setStagedRoommateEmail,
    setStagedRoommatePhone,
    handleToggleTheme,
    handleLogout,
    handleProfilePress,
    handleLinkedAccountsPress,
    handlePaymentMethodsPress,
    handleAutopayPress,
    handleHowItWorksPress,
    handleTutorialPress,
    handleDeleteAccountPress,
    handleFeedbackPress,
  } as const;
}
